import { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { TOTP_STATUS_QUERY_KEY, uploadQrCodeTotp } from '@/api/certificados';
import { notifyMutationError } from '@/lib/errors';
import { useNotification } from '@/context/NotificationProvider';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

interface QrCodeTotpUploaderProps {
  /** Disparado após upload bem-sucedido. */
  onSuccess?: () => void;
  /** Reduz espaçamentos para telas com pouco espaço. */
  compact?: boolean;
}

/**
 * Uploader puro da imagem de QR Code TOTP — sem card/título próprios.
 *
 * Usado como modo secundário dentro do {@link ConectarPdpjCard}: o advogado que
 * prefere compartilhar o QR do próprio aplicativo autenticador (Google
 * Authenticator, Authy, etc.) em vez de deixar o JurisApp provisionar um OTP
 * gerenciado. O segredo é decodificado no autenticador e nunca retorna na
 * resposta. Após o upload, atualiza o status de pareamento para o card pai
 * refletir o estado conectado.
 */
export function QrCodeTotpUploader({ onSuccess, compact = false }: QrCodeTotpUploaderProps) {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const resetFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(null);
    setPreviewUrl(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const acceptFile = (selected: File | null) => {
    if (!selected) {
      resetFile();
      return;
    }
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      showError('Formato inválido. Envie uma imagem PNG ou JPG.');
      return;
    }
    if (selected.size > MAX_BYTES) {
      showError('Imagem muito grande. O limite é de 2 MB.');
      return;
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0] ?? null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0] ?? null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      showError('Selecione a imagem do QR Code.');
      return;
    }
    try {
      setUploading(true);
      await uploadQrCodeTotp(file);
      showSuccess('QR Code enviado com sucesso.');
      resetFile();
      await queryClient.invalidateQueries({ queryKey: TOTP_STATUS_QUERY_KEY });
      onSuccess?.();
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-3' : 'space-y-4'}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={[
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-center transition-colors',
          dragOver
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/60 hover:bg-muted/40',
        ].join(' ')}
        aria-label="Selecionar imagem do QR Code"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Pré-visualização do QR Code"
            className="max-h-48 rounded-sm object-contain"
          />
        ) : (
          <>
            <Upload className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Clique ou arraste a imagem (PNG ou JPG, até 2 MB).
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>

      {file && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {file.name}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={resetFile}
            aria-label="Remover imagem selecionada"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Button type="submit" disabled={!file || uploading} className="w-full gap-2 sm:w-auto">
        {uploading ? (
          <>
            <Spinner size={16} className="text-inherit" />
            Enviando...
          </>
        ) : (
          <>
            <Upload className="h-4 w-4" />
            Enviar QR Code
          </>
        )}
      </Button>
    </form>
  );
}
